import { Chip, DialogTitle, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { gutter } from "./share_step_parts.js";

/** The steps of sharing, in order, as the header's chips show them. */
const shareSteps = ["choose", "who"] as const;

/**
 * The screen a share is at. Saying whether an earlier guest is the one shared
 * with is part of saying who, so it shows the second step's chip.
 */
export type ShareScreen = (typeof shareSteps)[number] | "earlier_guest";

/** Whom the share is about, which step it is at and what that screen asks. */
export const ShareHeader = ({
  personName,
  at,
}: {
  personName: string;
  at: ShareScreen;
}) => {
  const { t } = useTranslation();
  const atStep = at === "earlier_guest" ? "who" : at;
  const atIndex = shareSteps.indexOf(atStep);

  return (
    <DialogTitle component="div" sx={{ px: gutter }}>
      <Typography variant="body2" color="text.secondary">
        {t("shareWithGuest.titleFor", { personName })}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ my: 1 }}>
        {shareSteps.map((each, index) => (
          <Chip
            key={each}
            size="small"
            label={t(`shareWithGuest.steps.${each}`)}
            aria-current={each === atStep ? "step" : undefined}
            color={
              index < atIndex
                ? "success"
                : each === atStep
                  ? "primary"
                  : "default"
            }
            variant={index < atIndex ? "outlined" : "filled"}
          />
        ))}
      </Stack>
      <Typography variant="h6" component="h2">
        {t(
          `shareWithGuest.${at === "earlier_guest" ? "earlierGuest" : at}.heading`,
        )}
      </Typography>
      {at === "choose" ? (
        <Typography variant="body2" color="text.secondary">
          {t("shareWithGuest.choose.intro")}
        </Typography>
      ) : null}
    </DialogTitle>
  );
};
